(() => {
  const roots = document.querySelectorAll('[data-zz-pdp]');

  function setupSticky(root) {
    const sticky = root.querySelector('[data-pdp-sticky]');
    const primaryCta = root.querySelector('.zz-pdp__hero-copy .zz-pdp__button--primary');
    if (!sticky || !primaryCta || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', visible);
      document.documentElement.classList.toggle('zz-pdp-has-sticky', visible);
    }, { threshold: 0 });
    observer.observe(primaryCta);
  }

  function setupRenderer(root) {
    const dialog = root.querySelector('[data-pdp-render-dialog]');
    const openButtons = root.querySelectorAll('[data-pdp-render-open]');
    if (!dialog || !openButtons.length) return;

    const stage = dialog.querySelector('[data-pdp-render-stage]');
    const loading = dialog.querySelector('[data-pdp-render-loading]');
    const status = dialog.querySelector('[data-pdp-render-status]');
    const closeButton = dialog.querySelector('[data-pdp-render-close]');
    const rendererUrl = dialog.dataset.rendererUrl;
    const svgUrl = dialog.dataset.renderSvg;
    const productType = dialog.dataset.renderProduct || 'keychain';
    const isChinese = root.lang.toLowerCase().startsWith('zh');
    const rendererOrigin = new URL(rendererUrl, window.location.href).origin;
    let frame = null;
    let lastTrigger = null;
    let sessionId = 0;
    let fetchController = null;
    let readyTimer = 0;

    function setLoading(message) {
      loading.hidden = false;
      loading.textContent = message;
    }

    function clearReadyTimer() {
      if (!readyTimer) return;
      window.clearTimeout(readyTimer);
      readyTimer = 0;
    }

    function focusableElements() {
      return Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), iframe'))
        .filter((element) => !element.hidden && element.getClientRects().length > 0);
    }

    function releaseFrame() {
      clearReadyTimer();
      fetchController?.abort();
      fetchController = null;
      if (!frame) return;
      frame.contentWindow?.postMessage({ type: 'ZEKZEK_RENDER_DISPOSE' }, rendererOrigin);
      frame.remove();
      frame = null;
    }

    function closeDialog({ restoreFocus = true } = {}) {
      sessionId += 1;
      releaseFrame();
      dialog.hidden = true;
      loading.hidden = false;
      loading.textContent = isChinese ? '正在生成 3D 预览…' : 'Generating the 3D preview…';
      status.textContent = isChinese ? '拖动旋转，滚轮或双指缩放' : 'Drag to rotate. Scroll or pinch to zoom.';
      if (!document.querySelector('[data-pdp-render-dialog]:not([hidden])')) {
        document.body.classList.remove('zz-pdp-render-open');
      }
      if (restoreFocus && lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
      lastTrigger = null;
    }

    function waitForFrameLoad(targetFrame) {
      return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          cleanup();
          reject(new Error(isChinese ? '连接 3D 渲染器超时，请关闭后重试。' : 'The 3D renderer timed out. Close and try again.'));
        }, 12000);
        const cleanup = () => {
          window.clearTimeout(timeout);
          targetFrame.removeEventListener('load', handleLoad);
          targetFrame.removeEventListener('error', handleError);
        };
        const handleLoad = () => {
          cleanup();
          resolve();
        };
        const handleError = () => {
          cleanup();
          reject(new Error(isChinese ? '3D 渲染器加载失败，请关闭后重试。' : 'The 3D renderer failed to load. Close and try again.'));
        };
        targetFrame.addEventListener('load', handleLoad);
        targetFrame.addEventListener('error', handleError);
      });
    }

    async function openDialog(trigger) {
      if (!dialog.hidden || frame) return;
      const currentSession = sessionId + 1;
      sessionId = currentSession;
      lastTrigger = trigger;
      dialog.hidden = false;
      document.body.classList.add('zz-pdp-render-open');
      setLoading(isChinese ? '正在加载 3D 渲染器…' : 'Loading the 3D renderer…');
      status.textContent = isChinese ? '正在准备交互式 3D 示例' : 'Preparing the interactive 3D example';
      closeButton?.focus({ preventScroll: true });

      fetchController = new AbortController();
      const currentFrame = document.createElement('iframe');
      currentFrame.className = 'zz-pdp__render-frame';
      currentFrame.title = isChinese
        ? 'ZEKZEK 交互式 3D 亚克力渲染预览'
        : 'ZEKZEK interactive 3D acrylic render preview';
      currentFrame.setAttribute('allow', 'fullscreen');
      currentFrame.setAttribute('scrolling', 'no');
      currentFrame.tabIndex = 0;
      stage.append(currentFrame);
      frame = currentFrame;

      try {
        const svgRequest = fetch(svgUrl, {
          credentials: 'same-origin',
          signal: fetchController.signal
        }).then((response) => {
          if (!response.ok) throw new Error(isChinese ? `3D 示例文件加载失败（${response.status}）` : `The 3D example failed to load (${response.status}).`);
          return response.text();
        });
        const frameRequest = waitForFrameLoad(currentFrame);
        currentFrame.src = rendererUrl;
        const [svgText] = await Promise.all([svgRequest, frameRequest]);
        if (currentSession !== sessionId || dialog.hidden || frame !== currentFrame) return;
        setLoading(isChinese ? '正在生成 3D 预览…' : 'Generating the 3D preview…');
        currentFrame.contentWindow?.postMessage({
          type: 'ZEKZEK_RENDER_LOAD',
          svgText,
          backSvgText: '',
          printMode: 'single',
          name: productType === 'standee' ? 'ZEKZEK Standee Example' : 'ZEKZEK Keychain Example',
          productType
        }, rendererOrigin);
        readyTimer = window.setTimeout(() => {
          if (currentSession !== sessionId || dialog.hidden) return;
          setLoading(isChinese ? '3D 预览生成超时，请关闭后重试。' : 'The 3D preview timed out. Close and try again.');
          status.textContent = isChinese ? '渲染器暂时没有返回结果' : 'The renderer did not return a result';
        }, 15000);
      } catch (error) {
        if (error?.name === 'AbortError' || currentSession !== sessionId) return;
        setLoading(error instanceof Error ? error.message : String(error));
        status.textContent = isChinese ? '预览加载失败，关闭后可以重新尝试' : 'The preview failed to load. Close and try again.';
      }
    }

    function handleRendererMessage(event) {
      if (!frame || event.source !== frame.contentWindow || event.origin !== rendererOrigin) return;
      if (event.data?.type === 'ZEKZEK_RENDER_READY') {
        clearReadyTimer();
        frame.classList.add('is-ready');
        loading.hidden = true;
        status.textContent = isChinese ? '拖动旋转，滚轮或双指缩放' : 'Drag to rotate. Scroll or pinch to zoom.';
        frame.contentWindow?.postMessage({ type: 'ZEKZEK_RENDER_RESUME' }, rendererOrigin);
      }
      if (event.data?.type === 'ZEKZEK_RENDER_ERROR') {
        clearReadyTimer();
        setLoading(event.data.message || (isChinese ? '3D 预览生成失败，请关闭后重试。' : 'The 3D preview failed. Close and try again.'));
        status.textContent = isChinese ? '渲染器未能生成预览' : 'The renderer could not generate the preview';
      }
    }

    openButtons.forEach((button) => {
      button.addEventListener('click', () => openDialog(button));
    });
    dialog.querySelectorAll('[data-pdp-render-close]').forEach((button) => {
      button.addEventListener('click', () => closeDialog());
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (!frame || dialog.hidden) return;
      frame.contentWindow?.postMessage({
        type: document.hidden ? 'ZEKZEK_RENDER_PAUSE' : 'ZEKZEK_RENDER_RESUME'
      }, rendererOrigin);
    });
    window.addEventListener('message', handleRendererMessage);
    window.addEventListener('pagehide', () => closeDialog({ restoreFocus: false }));
  }

  roots.forEach((root) => {
    setupSticky(root);
    setupRenderer(root);
  });
})();
